/**
 * useWaAudioRecorder — lógica de grabación de voz para WaChatComposer.
 *
 * Maneja el ciclo completo: start → pause/resume → preview → send/cancel.
 * No depende del estado del composer más allá de los dos callbacks de output.
 */

import { useRef, useState } from 'react';
import { validateAudioBlob } from '@/lib/wa-utils';

interface Options {
  onSendMedia: (file: File, type: 'audio', duration: number) => void;
  onError: (message: string) => void;
}

export function useWaAudioRecorder({ onSendMedia, onError }: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);
  const previewBlobRef = useRef<Blob | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      previewBlobRef.current = null;
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.start(200);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsPaused(false);
      setIsPlayingPreview(false);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      onError('No se pudo acceder al micrófono');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
      const mime = mediaRecorderRef.current.mimeType || 'audio/webm';
      previewBlobRef.current = new Blob(chunksRef.current, { type: mime });
    }
  };

  const resumeRecording = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
      setIsPlayingPreview(false);
    }
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    }
  };

  const playPreview = () => {
    if (!previewBlobRef.current) return;
    const url = URL.createObjectURL(previewBlobRef.current);
    const audio = new Audio(url);
    audioPreviewRef.current = audio;
    setIsPlayingPreview(true);
    audio.onended = () => {
      setIsPlayingPreview(false);
      audioPreviewRef.current = null;
      URL.revokeObjectURL(url);
    };
    audio.play();
  };

  const stopPreview = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
      setIsPlayingPreview(false);
    }
  };

  const sendRecording = () => {
    if (audioPreviewRef.current) { audioPreviewRef.current.pause(); audioPreviewRef.current = null; }
    const recorder = mediaRecorderRef.current;
    const capturedDuration = recordingTime;
    const chunks = [...chunksRef.current];
    if (recorder && (recorder.state === 'recording' || recorder.state === 'paused')) {
      recorder.onstop = () => {
        const mime = recorder.mimeType || 'audio/webm';
        const ext = mime.includes('ogg') ? 'ogg' : 'webm';
        const allChunks = [...chunks, ...chunksRef.current];
        const blob = new Blob(allChunks, { type: mime });
        const audioErr = validateAudioBlob(blob.size);
        if (audioErr) {
          onError(audioErr);
        } else {
          const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: mime });
          onSendMedia(file, 'audio', capturedDuration);
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        chunksRef.current = [];
      };
      chunksRef.current = [];
      recorder.requestData();
      recorder.stop();
    }
    mediaRecorderRef.current = null;
    previewBlobRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    setIsPlayingPreview(false);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  };

  const cancelRecording = () => {
    if (audioPreviewRef.current) { audioPreviewRef.current.pause(); audioPreviewRef.current = null; }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      try { mediaRecorderRef.current.stop(); } catch { /* already stopped */ }
      mediaRecorderRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    chunksRef.current = [];
    previewBlobRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    setIsPlayingPreview(false);
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
  };

  return {
    isRecording,
    isPaused,
    isPlayingPreview,
    recordingTime,
    startRecording,
    pauseRecording,
    resumeRecording,
    playPreview,
    stopPreview,
    sendRecording,
    cancelRecording,
  };
}
